import { Spin } from "antd";
import { FileText } from "react-feather";
import { Card, CardBody, Col } from "reactstrap";
import { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import dayjs from 'dayjs';
import MonthlyAttendanceCardHeader from "./MonthlyAttendanceCardHeader";
import MonthlyAttendanceCardFooter from "./MonthlyAttendanceCardFooter";
import { attendanceChartData } from "../ChartsData/ApexChart";
import { H5 } from "../../../../AbstractElements";

const getCourseLabel = (course) => `${course.name} (${course.type})`;

const MonthlyAttendance = ({ allAttendance, allStudents, allCourses, loading }) => {
  const activeCourses = allCourses?.filter(course => course.isCurrent);
  const activeCourseIds = new Set(activeCourses?.map(course => course.id));
  const [selectedCourseId, setSelectedCourseId] = useState('');

  useEffect(() => {
    if(selectedCourseId) return;
    setSelectedCourseId(activeCourses?.[0]?.id);
  }, [activeCourses]);

  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();

  const coursePresentPercentage = {};

  const courseIdNameMap = {};
  activeCourses.forEach((course) => {
    courseIdNameMap[course.id] = getCourseLabel(course);
  });

  const studentCourseMap = new Map();
  allStudents?.forEach(student => {
    const courseId = student?.courses[student?.courseIndex]?.courseId;
    if (activeCourseIds.has(courseId)) {
      studentCourseMap.set(student.email, courseId);
    }
  });

  const totalAttendance = allAttendance?.filter(att => {
    const courseId = studentCourseMap.get(att.userId);
    if (!courseId) return false;

    const attDate = new Date(att.attendanceDate);
    return (
      attDate.getMonth() === currentMonth &&
      attDate.getFullYear() === currentYear
    );
  });

  const dayCourseWiseAttendance = {};
  const dayLabelsSet = new Set();

  totalAttendance?.forEach((att) => {
    const courseId = studentCourseMap.get(att.userId);
    const courseName = courseIdNameMap[courseId];
    const status = att.status.toLowerCase();
    if (!courseName) return;

    const date = new Date(att.attendanceDate);
    const day = dayjs(date).format("DD MMM");

    if (!dayCourseWiseAttendance[day]) {
      dayCourseWiseAttendance[day] = {};
    }
    if (!dayCourseWiseAttendance[day][courseName]) {
      dayCourseWiseAttendance[day][courseName] = { present: 0, absent: 0, leave: 0 };
    }
    dayCourseWiseAttendance[day][courseName][status]++;
    dayLabelsSet.add(day);

    if (!coursePresentPercentage[courseName]) {
      coursePresentPercentage[courseName] = {};
    }
    if (!coursePresentPercentage[courseName][day]) {
      coursePresentPercentage[courseName][day] = { presents: 0, total: 0 };
    }

    if (status === "present") {
      coursePresentPercentage[courseName][day].presents++;
    }
    coursePresentPercentage[courseName][day].total++;
  });


  const dayLabels = Array.from(dayLabelsSet).sort(
    (a, b) => dayjs(a, "DD MMM").toDate() - dayjs(b, "DD MMM").toDate()
  );

  const filteredCourseNames = Object.entries(courseIdNameMap)
    .filter(([id]) =>
      selectedCourseId === "All Courses" ? true : id === selectedCourseId
    )
    .map(([_, name]) => name);

  const statusColors = ['#00E396', '#FF4560', '#FEB019'];
  const statuses = ['present', 'absent', 'leave'];
  const series = statuses.map((status, statusIndex) => {
    const data = dayLabels.flatMap(day => {
      return filteredCourseNames.map(courseName => {
        const value = dayCourseWiseAttendance[day]?.[courseName]?.[status] || 0;
        return value > 0 ? value : null;
      });
    });
    return {
      name: status.charAt(0).toUpperCase() + status.slice(1),
      data: data,
    };
  });

  const coursePresentStats = Object.entries(coursePresentPercentage).map(([courseName, dailyStats]) => {
    let totalPercentage = 0;
    let dayCount = 0;

    Object.values(dailyStats).forEach(({ presents, total }) => {
      if (total > 0) {
        totalPercentage += (presents / total) * 100;
        dayCount++;
      }
    });

    const averagePercentage = dayCount > 0 ? parseFloat((totalPercentage / dayCount).toFixed(2)) : 0;

    return {
      courseName,
      percentage: averagePercentage,
    };
  });

  const overallPercentage = coursePresentStats.length
  ? parseFloat(
      (
        coursePresentStats.reduce((sum, stat) => sum + stat.percentage, 0) /
        coursePresentStats.length
      ).toFixed(2)
    )
  : 0;

  const chartData = attendanceChartData(series, dayLabels, statusColors, filteredCourseNames);

  return (
    <Col xl="6" lg="6" md="12" className="box-col-30">
      <Card className="our-earning relative">
        {loading && <div className={`charts-initial-loading`}><Spin /></div>}
        <MonthlyAttendanceCardHeader overallPercentage={overallPercentage} selectedCourseId={selectedCourseId} setSelectedCourseId={setSelectedCourseId} />
        {
          !totalAttendance.length ? 
            <div className="d-flex justify-content-center align-items-center text-center m-3" style={{minHeight: '350px'}}>
             <div>
                <FileText size={50} />
                <H5>No attendance has been recorded for the current month.</H5>
              </div>
            </div>
            :
            <CardBody className="p-0">
              <Chart options={chartData.options} series={chartData.series} type="bar" height={350} />
            </CardBody>
        }
        <MonthlyAttendanceCardFooter coursePresentStats={coursePresentStats} />
      </Card>
    </Col>
  );
};

export default MonthlyAttendance;